import {
  type Task,
  formatDayName,
  formatDayNumber,
  formatMonth,
} from "@taskfortress/core"
import React, { useMemo } from "react"
import { AddTaskButton } from "../AddTaskButton"
import { Box } from "../Box"
import { Text } from "../Text"
import { TaskItem } from "./TaskItem"
import { groupTasksByStatus } from "./taskGrouping"

interface DayColumnProps {
  date: string
  tasks: Task[]
  className?: string
}

export const DayColumn: React.FC<DayColumnProps> = ({
  date,
  tasks,
  className,
}) => {
  // Memoize tasks by status for this date
  const tasksByStatus = useMemo(() => {
    return groupTasksByStatus(tasks)
  }, [tasks])

  const hasDoneTasks = useMemo(
    () => tasksByStatus.done.length > 0,
    [tasksByStatus],
  )

  return (
    <Box className={className}>
      <Box className="h-full rounded-lg border border-outline-200 shadow-sm bg-background-0 overflow-hidden mx-5">
        <Box className="p-3 items-center bg-background-50 relative">
          <Text className="font-medium text-typography-900">
            {formatDayName(date)}
          </Text>
          <Text className="text-lg font-bold text-typography-900">
            {formatDayNumber(date)}
          </Text>
          <Text className="text-xs text-typography-600">
            {formatMonth(date)}
          </Text>
          <Box className="absolute bottom-2 right-2">
            <AddTaskButton date={date} small />
          </Box>
        </Box>

        <Box className="flex-1 p-2">
          {tasks.length === 0 ? (
            <Box className="items-center justify-center py-10">
              <Text className="text-center text-typography-500 text-sm italic">
                No tasks for this day
              </Text>
            </Box>
          ) : (
            <>
              {/* Display todos first */}
              {tasksByStatus.todo.map((task) => (
                <TaskItem key={task.id} task={task} />
              ))}

              {/* Display done tasks */}
              {hasDoneTasks && (
                <Box className="my-2 border-t border-outline-200 pt-2">
                  <Text className="text-xs text-typography-600 pl-1 mb-1">
                    Completed
                  </Text>
                  {tasksByStatus.done.map((task) => (
                    <TaskItem key={task.id} task={task} />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
